import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BRAND } from "@/lib/config";
import { FileText, Phone, Mail } from "lucide-react";

const SECTIONS = [
  {
    n: 1,
    title: "Scope of Services",
    body: [
      "The Company provides professional residential and commercial trash can maintenance services to the Client.",
    ],
    bullets: [
      "Services include high-pressure washing, sanitizing, and deodorizing of both the interior and exterior of designated trash cans using eco-friendly cleaning solutions to clear dirt, grime, bacteria, and severe odors.",
      "Services incorporate the strict containment and responsible removal of all hazardous waste water generated during the cleaning process, handled according to local environmental regulations.",
    ],
  },
  {
    n: 2,
    title: "Service Plans, Pricing, & Term",
    bullets: [
      "Monthly Servicing Plans: Services are rendered on a recurring monthly timeline to ensure cans stay clean all year long.",
      "Standard Pricing Structure: 1 Can Plan — $25.00 per month; 2 Cans Plan — $40.00 per month; Monthly Servicing Base Rate (Standard Plan) — $35.00 per month.",
      "This Agreement serves as a monthly subscription and automatically renews continuously unless terminated or suspended by either party.",
    ],
  },
  {
    n: 3,
    title: "Recurring Payment Methods & Billing Authorization",
    bullets: [
      "Payment Authorization: By enrolling in a subscription plan, the Client explicitly authorizes the Company to securely store their designated billing credentials and process recurring automatic charges.",
      "Authorized Payment Methods: Accepted payment streams include credit card auto-draft setups, debit card pre-authorizations, and Automated Clearing House (ACH) direct bank transfers.",
      "Billing Schedule: Payments are due monthly upon receipt of invoice, or automatically deducted on the Client's recurring monthly billing cycle date.",
      "Non-Payment & Suspensions: The Company reserves the right to immediately suspend or permanently cancel any scheduled service loops if an automatic transaction fails or if an outstanding invoice remains unpaid past its initial receipt window.",
    ],
  },
  {
    n: 4,
    title: "Cancellation & Termination Notice",
    bullets: [
      "Voluntary Termination: Either party may freely choose to terminate this Agreement at any point.",
      "Notice Requirement: To avoid subsequent billing cycles, a minimum of 7 days' advance notice must be provided before the next scheduled billing date.",
      "Late Notices: If notice is given with fewer than 7 days remaining before the next cycle, the upcoming transaction will process as standard, and final service will be performed during that concluding period.",
    ],
  },
  {
    n: 5,
    title: "Out-of-Town Rescheduling & Skip Notifications",
    bullets: [
      "Client Absence Protocol: If the Client is planning to travel or be out of town and wishes to skip or delay a scheduled cleaning event, a formal notice must be sent to the Company.",
      "Notification Window: Skip or rescheduling requests must be submitted at least 48 hours prior to the Client's weekly/monthly scheduled cleaning day by calling or texting 540-384-0797, or by emailing the official customer support portal.",
      "Credit Adjustments: Timely notifications will result in an applicable service credit applied to the next regular invoice cycle. If the Client fails to provide 48 hours' notice and the technician arrives at the property, no credits or refunds will be issued for that skipped event.",
    ],
  },
  {
    n: 6,
    title: "Prohibited Waste Items",
    bullets: [
      "Heavy Restriction Standards: To preserve equipment safety, operator health, and environmental compliance, the Client is strictly forbidden from placing certain materials into the waste bins prior to a scheduled cleaning session.",
      "Prohibited Substances — bins containing any of the following will not be serviced under any circumstances: human waste, animal feces, or diapers; wet paint, solvents, thinners, or stains; motor oil, gasoline, diesel, or flammable automotive fluids; chemical pesticides, fertilizers, or toxic weed killers; corrosive acids, lithium batteries, or explosive fireworks; biohazardous materials, medical waste, needles, or syringes.",
      "Service Postponements: If any prohibited substances are discovered inside a bin on service day, the technician will skip the bin entirely. The service loop for that month will be forfeited, and no refund will be issued.",
    ],
  },
  {
    n: 7,
    title: "Access, Responsibility, and Liability",
    bullets: [
      "Accessibility Requirements: Bins must be empty of loose debris, completely unlocked, and positioned at an accessible outdoor location (such as the curb, driveway, or side of the garage) by 7:00 AM on the designated service day.",
      "Unbagged Trash Policy: The Company washes bins but does not dump unbagged raw trash. If a bin contains heavy unbagged garbage, it will not be washed, and no refund will be granted.",
      "Liability Limitations: The Company is not liable for pre-existing structural issues, cracks, missing wheels, or weathered damage on the bins. The Client assumes full indemnity regarding claims resulting from undisclosed hazardous items hidden within the cans.",
    ],
  },
  {
    n: 8,
    title: "Governing Law",
    bullets: [
      "Legal Jurisdiction: This Agreement shall be governed, interpreted, and enforced solely in accordance with the regulations and laws of the State of Virginia, within which BOCO BINS LLC proudly serves its local communities.",
    ],
  },
];

export default function Terms() {
  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <Navbar />

      <header className="relative overflow-hidden bg-boco-navy text-white">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 py-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold tracking-[0.15em] text-boco-green">
            <FileText className="h-3.5 w-3.5" /> LEGAL
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl font-black tracking-tight">Terms of Service</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            This Terms of Service Agreement ("Agreement") is entered into by and between BOCO BINS LLC
            ("Company"), accessible via phone at 540-384-0797, and the subscribing customer ("Client"). By
            subscribing to, paying for, or utilizing the trash can cleaning services, the Client agrees to be
            bound entirely by the following terms and conditions.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 sm:px-8 py-14" data-testid="terms-content">
        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.n} id={`section-${s.n}`} data-testid={`terms-section-${s.n}`} className="scroll-mt-28">
              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-boco-navy flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-boco-green text-boco-navy text-sm font-black">
                  {s.n}
                </span>
                <span className="uppercase tracking-tight">{s.title}</span>
              </h2>
              {s.body?.map((p, i) => (
                <p key={i} className="mt-4 pl-11 text-slate-600 leading-relaxed">{p}</p>
              ))}
              <ul className="mt-4 pl-11 space-y-3">
                {s.bullets.map((b, i) => (
                  <li key={i} className="relative pl-5 text-slate-600 leading-relaxed">
                    <span className="absolute left-0 top-2.5 h-1.5 w-1.5 rounded-full bg-boco-green" />
                    {b}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-14 rounded-3xl bg-white border border-slate-200 p-8 shadow-sm">
          <h3 className="font-display text-lg font-extrabold text-boco-navy">Questions about these terms?</h3>
          <p className="mt-2 text-slate-600">Reach out any time — we're happy to help.</p>
          <div className="mt-5 flex flex-wrap gap-4">
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-boco-navy px-6 py-3 font-bold text-white hover:bg-boco-navylight transition-colors" data-testid="terms-phone">
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 font-bold text-boco-navy hover:bg-slate-50 transition-colors" data-testid="terms-email">
              <Mail className="h-4 w-4" /> {BRAND.email}
            </a>
          </div>
          <div className="mt-6">
            <Link to="/book" className="inline-flex rounded-full bg-boco-green px-6 py-3 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors" data-testid="terms-book-btn">
              Book Your Cleaning
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
